import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { formatDate } from '../../utils/dateFormatter';
import { useLanguage } from '../../hooks/useLanguage';
import { useGetAdminNumbersQuery, useGetServerLabelsQuery } from '../../store/api/adminApi';
import {
  flattenServerLabelGroups,
  resolveProviderFilterLabel,
} from '../../utils/serverDisplayNames';

type SourceTableFilter = 'all' | 'permanent' | 'smspool_temp' | 'durian_temp';
type ServiceProviderFilter = 'all' | 'textverified' | 'pvadeals' | 'smspool' | 'durianRcs';

const SOURCE_LABELS_AR: Record<Exclude<SourceTableFilter, 'all'>, string> = {
  permanent: 'أرقام دائمة',
  smspool_temp: 'أرقام مؤقتة — سيرفر 1',
  durian_temp: 'أرقام مؤقتة — سيرفر 2',
};

export function AdminNumbersPage() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [searchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get('userId') ?? '';

  const [page, setPage] = useState(1);
  const [sourceTable, setSourceTable] = useState<SourceTableFilter>('all');
  const [serviceProvider, setServiceProvider] = useState<ServiceProviderFilter>('all');
  const [userId, setUserId] = useState(userIdFromUrl);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const { data: serverLabelsData } = useGetServerLabelsQuery();
  const serverLabelRows = flattenServerLabelGroups(serverLabelsData?.data);

  const { data, isLoading, error, refetch } = useGetAdminNumbersQuery({
    page,
    limit: 20,
    sourceTable,
    serviceProvider: serviceProvider !== 'all' ? serviceProvider : undefined,
    userId: userId || undefined,
    status: status || undefined,
    search: search || undefined,
    sortBy: 'purchasedAt',
    sortOrder: -1,
  });

  const numbers = data?.data.numbers ?? [];
  const pagination = data?.data.pagination;

  const providerLabel = (provider?: string | null) => {
    if (!provider) return '-';
    return resolveProviderFilterLabel(provider, serverLabelRows);
  };

  const sourceLabel = (source?: string | null) => {
    if (!source) return '-';
    if (source === 'permanent' || source === 'SmspoolTemporaryNumber' || source === 'smspool_temp') {
      if (source === 'permanent') return SOURCE_LABELS_AR.permanent;
      return SOURCE_LABELS_AR.smspool_temp;
    }
    if (source === 'DurianTemporaryNumber' || source === 'durian_temp') {
      return SOURCE_LABELS_AR.durian_temp;
    }
    // Never show raw collection/provider names to merchants
    if (/smspool/i.test(source)) return SOURCE_LABELS_AR.smspool_temp;
    if (/durian/i.test(source)) return SOURCE_LABELS_AR.durian_temp;
    if (/permanent|textverified/i.test(source)) return SOURCE_LABELS_AR.permanent;
    return source.replace(/TemporaryNumber|PermanentNumber/gi, '').trim() || 'رقم';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="text-center py-16">
          <p className="text-neutral-medium text-xl font-body animate-pulse">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
        <h2 className="text-2xl font-heading font-bold text-danger mb-4">{t('errors.network')}</h2>
        <button
          onClick={() => void refetch()}
          className="px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen animate-slide-up">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
          {t('admin.numbers.title')}
        </h1>
        <p className="text-neutral-medium text-lg font-body">{t('admin.numbers.subtitle')}</p>
      </div>

      <div className="card-glass rounded-lg p-4 mb-6 border border-neutral-dark">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <select
            value={sourceTable}
            onChange={(e) => { setSourceTable(e.target.value as SourceTableFilter); setPage(1); }}
            className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
          >
            <option value="all">{t('admin.numbers.filters.allSources')}</option>
            <option value="permanent">{SOURCE_LABELS_AR.permanent}</option>
            <option value="smspool_temp">{SOURCE_LABELS_AR.smspool_temp}</option>
            <option value="durian_temp">{SOURCE_LABELS_AR.durian_temp}</option>
          </select>
          <select
            value={serviceProvider}
            onChange={(e) => { setServiceProvider(e.target.value as ServiceProviderFilter); setPage(1); }}
            className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
          >
            <option value="all">{t('admin.numbers.filters.allProviders') || 'كل المزودين'}</option>
            <option value="textverified">{providerLabel('textverified')}</option>
            <option value="pvadeals">{providerLabel('pvadeals')}</option>
            <option value="smspool">{providerLabel('smspool')}</option>
            <option value="durianRcs">{providerLabel('durianRcs')}</option>
          </select>
          <input
            value={userId}
            onChange={(e) => { setUserId(e.target.value); setPage(1); }}
            placeholder={t('admin.numbers.filters.userId')}
            className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
          />
          <input
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            placeholder={t('admin.numbers.filters.status')}
            className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
          />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder={t('admin.numbers.filters.search')}
            className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
          />
        </div>
      </div>

      <div className="space-y-3">
        {numbers.map((item) => (
          <div key={item._id} className="card-glass rounded-xl p-4 border border-neutral-dark/50">
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0">
                <p className="text-neutral-light font-heading font-semibold break-all">{item.number}</p>
                <p className="text-xs text-neutral-medium font-body">{sourceLabel(item.sourceTable)}</p>
                <p className="text-sm text-neutral-medium font-body">
                  {providerLabel(item.serviceProvider) || item.serviceName || '-'} - {item.countryName || '-'}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-brand-accent font-heading font-semibold">
                  {typeof item.price === 'number' ? `$${item.price.toFixed(2)}` : '-'}
                </p>
                <p className="text-xs text-neutral-medium">{item.status || '-'}</p>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs">
              <Link
                to={`/admin/users/${item.userId}`}
                className="text-brand-accent hover:underline font-body"
              >
                {t('admin.numbers.user')}: {String(item.userId)}
              </Link>
              <span className="text-neutral-medium font-body">
                {item.purchasedAt ? formatDate(item.purchasedAt, currentLanguage) : '-'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-4 py-2 rounded-lg bg-neutral-dark text-neutral-light disabled:opacity-40"
          >
            {t('common.previous')}
          </button>
          <span className="px-3 py-2 text-neutral-medium font-body">
            {page} / {pagination.totalPages}
          </span>
          <button
            disabled={page >= pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-4 py-2 rounded-lg bg-neutral-dark text-neutral-light disabled:opacity-40"
          >
            {t('common.next')}
          </button>
        </div>
      )}
    </div>
  );
}
